// Projects landing visual overview — KPI strip + donut + pipeline + mini Gantt.
// Hand-built SVG/CSS only (no chart library). Every value is derived from the
// project records passed in (already client-scoped + filtered by the page).
import { PieChart, BarChart3, CalendarRange, CheckCircle2 } from "lucide-react";

export const STAGE_BUCKETS = ["Enquiry", "RIBA 2-3 Design", "Technical design", "Construction", "Handover"];
const BUCKET_COLORS = ["#1f2a44", "#C75A1B", "#2f7d6f", "#d9a441", "#6B4E9E"];
const SLICE_COLORS = ["#1f2a44", "#C75A1B", "#2f7d6f", "#d9a441", "#9aa6b8", "#6B4E9E", "#3a4a6b", "#b8541a"];

// Map a raw workflow_stage code to one of the five pipeline buckets.
export function stageBucket(code) {
    const s = (code || "").toLowerCase();
    if (/riba2|riba_2|riba3|riba_3/.test(s)) return "RIBA 2-3 Design";
    if (/riba4|technical|pre_construction|preconstruction/.test(s)) return "Technical design";
    if (/riba5|construction|site_visit|mobilis/.test(s)) return "Construction";
    if (/riba6|handover|occupation|completion|complete|reporting|qa|client_issue|closed/.test(s)) return "Handover";
    return "Enquiry"; // enquiry, setup, project_setup, riba1, brief, default
}

function gbpK(v) {
    if (!v) return "£0";
    if (v >= 10000) return `£${Math.round(v / 1000)}k`;
    if (v >= 1000) return `£${(v / 1000).toFixed(1)}k`;
    return `£${v}`;
}
const trunc = (s, n = 22) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");
const parseDate = (d) => (d ? new Date(d + "T00:00:00") : null);

export default function ProjectsOverview({ projects = [], clientMap = {}, groupByClient = false, loading = false, hideFinancials = false, onOpen, onFilterClient, onFilterBucket }) {
    if (loading) return <OverviewSkeleton />;
    if (!projects.length) return null;

    // Single summed-once portfolio total — shared by the KPI tile AND the donut
    // centre so the two always read identically (no off-by-rounding mismatch).
    const total = projects.reduce((s, p) => s + (p.value_gbp || 0), 0);
    const activeCount = projects.filter((p) => p.status && !["Complete", "Cancelled", "On Hold"].includes(p.status)).length;
    const onHoldCount = projects.filter((p) => p.status === "On Hold").length;
    const enquiryProjects = projects.filter((p) => stageBucket(p.workflow_stage) === "Enquiry");
    const enquiryValue = enquiryProjects.reduce((s, p) => s + (p.value_gbp || 0), 0);

    // Next target — earliest target_date amongst non-complete projects
    const upcoming = projects
        .filter((p) => p.target_date && p.status !== "Complete" && p.status !== "Cancelled")
        .sort((a, b) => a.target_date.localeCompare(b.target_date));
    const nextTarget = upcoming[0] || null;
    const nextTargetLabel = nextTarget
        ? parseDate(nextTarget.target_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        : "—";

    return (
        <div className="space-y-5" data-testid="projects-overview">
            {/* KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="projects-kpi-strip">
                {hideFinancials
                    ? <Kpi testId="kpi-portfolio-value" label="Projects" value={projects.length} sub={`In your portal`} />
                    : <Kpi testId="kpi-portfolio-value" label="Portfolio value" value={gbpK(total)} sub={`Across ${projects.length} live project${projects.length === 1 ? "" : "s"}`} />}
                <Kpi testId="kpi-active" label="Active" value={activeCount} sub="In delivery" valueColor="#2E7D4F" />
                <Kpi testId="kpi-on-hold" label="On hold" value={onHoldCount} sub="Awaiting go-ahead" valueColor="#C68A1C" />
                <Kpi testId="kpi-enquiry" label="In enquiry stage" value={enquiryProjects.length} sub={hideFinancials ? "Early stage" : `${gbpK(enquiryValue)} pipeline`} />
                <Kpi testId="kpi-next-target" label="Next target" value={nextTargetLabel} sub={nextTarget ? trunc(nextTarget.name, 26) : "Nothing scheduled"} small />
            </div>

            {/* Donut + Pipeline */}
            <div className={`grid ${hideFinancials ? "" : "lg:grid-cols-2"} gap-5 items-stretch`}>
                {!hideFinancials && <DonutCard projects={projects} total={total} clientMap={clientMap} groupByClient={groupByClient} onOpen={onOpen} onFilterClient={onFilterClient} />}
                <PipelineCard projects={projects} onFilterBucket={onFilterBucket} hideFinancials={hideFinancials} />
            </div>

            {/* Delivery timeline */}
            <TimelineCard projects={projects} clientMap={clientMap} onOpen={onOpen} hideFinancials={hideFinancials} />

            <style>{`
                @media (prefers-reduced-motion: reduce) {
                    [data-testid="projects-overview"] * { transition: none !important; animation: none !important; }
                }
            `}</style>
        </div>
    );
}

function Kpi({ label, value, sub, valueColor = "#1F1F1F", testId, small }) {
    return (
        <div data-testid={testId} className="bg-white border rounded-[13px] shadow-brand-sm px-4 py-3.5" style={{ borderColor: "#E5E0DA" }}>
            <div className="text-[10px] font-bold uppercase tracking-[.07em]" style={{ color: "#6F6A64" }}>{label}</div>
            <div className={`${small ? "text-[18px]" : "text-[26px]"} font-bold leading-tight mt-1.5 truncate`} style={{ color: valueColor }} title={String(value)}>{value}</div>
            <div className="text-[11px] mt-1 truncate" style={{ color: "#9A968F" }} title={sub}>{sub}</div>
        </div>
    );
}

function CardShell({ icon: Icon, title, aside, children, testId }) {
    return (
        <div data-testid={testId} className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden h-full flex flex-col" style={{ borderColor: "#E5E0DA" }}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: "#F0EDE8" }}>
                <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                    <h3 className="text-[14px] font-bold text-[#1F1F1F]">{title}</h3>
                </div>
                {aside && <span className="text-[11px]" style={{ color: "#9A968F" }}>{aside}</span>}
            </div>
            <div className="p-5 flex-1">{children}</div>
        </div>
    );
}

// Thin skeleton shown while project data is loading (never a broken/empty frame).
function SkelCard({ className = "", style, children }) {
    return <div className={`bg-white border rounded-[13px] shadow-brand-sm ${className}`} style={{ borderColor: "#E5E0DA", ...style }}>{children}</div>;
}
function OverviewSkeleton() {
    return (
        <div className="space-y-5 animate-pulse" data-testid="projects-overview-skeleton">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {Array.from({ length: 5 }).map((_, i) => (
                    <SkelCard key={i} className="px-4 py-3.5">
                        <div className="h-2.5 w-16 rounded bg-[#ECE7E0]" />
                        <div className="h-5 w-20 rounded bg-[#E2DCD3] mt-2.5" />
                        <div className="h-2 w-24 rounded bg-[#ECE7E0] mt-2" />
                    </SkelCard>
                ))}
            </div>
            <div className="grid lg:grid-cols-2 gap-5 items-stretch">
                <SkelCard className="p-5" style={{ minHeight: 220 }}>
                    <div className="flex items-center gap-6">
                        <div className="w-[150px] h-[150px] rounded-full border-[26px] shrink-0" style={{ borderColor: "#EFEAE3" }} />
                        <div className="flex-1 space-y-2.5">
                            {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-3 rounded bg-[#ECE7E0]" />)}
                        </div>
                    </div>
                </SkelCard>
                <SkelCard className="p-5 space-y-3.5" style={{ minHeight: 220 }}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <div className="w-[118px] h-6 rounded bg-[#ECE7E0]" />
                            <div className="flex-1 h-7 rounded-full bg-[#F1ECE5]" />
                        </div>
                    ))}
                </SkelCard>
            </div>
            <SkelCard className="p-5 space-y-3">
                {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-10 rounded bg-[#F1ECE5]" />)}
            </SkelCard>
        </div>
    );
}

function DonutCard({ projects, total, clientMap, groupByClient, onOpen, onFilterClient }) {
    // Build slices
    let slices;
    if (groupByClient) {
        const byClient = {};
        projects.forEach((p) => {
            const cid = p.client_id;
            if (!byClient[cid]) byClient[cid] = { key: cid, label: clientMap[cid]?.name || "Unknown", value: 0, clientId: cid };
            byClient[cid].value += p.value_gbp || 0;
        });
        slices = Object.values(byClient);
    } else {
        slices = projects.map((p) => ({ key: p.id, label: p.name, value: p.value_gbp || 0, projectId: p.id }));
    }
    slices = slices.filter((s) => s.value > 0).sort((a, b) => b.value - a.value);

    // Collapse to top 5 + Other when more than 6
    if (slices.length > 6) {
        const top = slices.slice(0, 5);
        const rest = slices.slice(5);
        const otherVal = rest.reduce((s, x) => s + x.value, 0);
        slices = [...top, { key: "__other", label: "Other", value: otherVal, other: true }];
    }
    slices = slices.map((s, i) => ({ ...s, color: SLICE_COLORS[i % SLICE_COLORS.length] }));

    const count = groupByClient ? slices.filter((s) => !s.other).length : projects.length;
    const clickSlice = (s) => {
        if (s.other) return;
        if (s.projectId && onOpen) onOpen(s.projectId);
        else if (s.clientId && onFilterClient) onFilterClient(s.clientId);
    };

    return (
        <CardShell icon={PieChart} title={groupByClient ? "Value by client" : "Portfolio value by project"}
            aside={groupByClient ? "click a slice to filter" : (count > 6 ? "top 5 + other" : "click to open")} testId="projects-donut-card">
            <div className="flex items-center gap-6 flex-wrap">
                <Donut slices={slices} total={total} centerLabel={gbpK(total)} centerSub={groupByClient ? `${count} CLIENTS` : `${count} PROJECTS`} />
                <ul className="flex-1 min-w-[180px] space-y-2.5">
                    {slices.map((s) => (
                        <li key={s.key} data-testid={`donut-legend-${s.other ? "other" : (s.projectId || s.clientId)}`}
                            onClick={() => clickSlice(s)}
                            className={`flex items-center gap-2.5 text-[13px] ${s.other ? "" : "cursor-pointer hover:opacity-70 transition-opacity"}`}>
                            <span className="w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ background: s.color }} />
                            <span className="flex-1 min-w-0 truncate text-[#2D2D2D]" title={s.label}>{trunc(s.label, 26)}</span>
                            <span className="font-mono font-semibold text-[#1F1F1F]">{gbpK(s.value)}</span>
                        </li>
                    ))}
                    {slices.length === 0 && <li className="text-[12px] italic" style={{ color: "#9A968F" }}>No project value to chart.</li>}
                </ul>
            </div>
        </CardShell>
    );
}

function Donut({ slices, total, centerLabel, centerSub }) {
    const size = 150, stroke = 26, r = (size - stroke) / 2, c = 2 * Math.PI * r;
    let offset = 0;
    return (
        <div className="relative shrink-0" style={{ width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
                    {total > 0 && slices.length ? slices.map((s) => {
                        const len = (s.value / total) * c;
                        const el = (
                            <circle key={s.key} cx={size / 2} cy={size / 2} r={r} fill="none"
                                stroke={s.color} strokeWidth={stroke}
                                strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset} />
                        );
                        offset += len;
                        return el;
                    }) : (
                        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EFEAE3" strokeWidth={stroke} />
                    )}
                </g>
            </svg>
            <div className="absolute inset-0 grid place-items-center text-center">
                <div>
                    <div className="text-[22px] font-bold leading-none text-[#1F1F1F]">{centerLabel}</div>
                    <div className="text-[9px] font-bold tracking-[.12em] mt-1" style={{ color: "#9A968F" }}>{centerSub}</div>
                </div>
            </div>
        </div>
    );
}

function PipelineCard({ projects, onFilterBucket, hideFinancials = false }) {
    const buckets = STAGE_BUCKETS.map((label, i) => {
        const inBucket = projects.filter((p) => stageBucket(p.workflow_stage) === label);
        return { label, color: BUCKET_COLORS[i], count: inBucket.length, value: inBucket.reduce((s, p) => s + (p.value_gbp || 0), 0) };
    });
    const maxCount = Math.max(1, ...buckets.map((b) => b.count));

    return (
        <CardShell icon={BarChart3} title="Pipeline by stage" aside="click to filter list" testId="projects-pipeline-card">
            <div className="space-y-3.5">
                {buckets.map((b) => {
                    const widthPct = b.count > 0 ? Math.max((b.count / maxCount) * 100, 14) : 0;
                    const isEmpty = b.count === 0;
                    return (
                        <div key={b.label} data-testid={`pipeline-bucket-${b.label.replace(/\s+/g, "-").toLowerCase()}`}
                            onClick={() => b.count > 0 && onFilterBucket && onFilterBucket(b.label)}
                            className={`flex items-center gap-3 transition-opacity ${b.count > 0 ? "cursor-pointer" : "opacity-60"}`}>
                            <div className="w-[118px] shrink-0">
                                <div className="text-[12.5px] font-semibold leading-tight" style={{ color: isEmpty ? "#B7B1A8" : "#1F1F1F" }}>{b.label}</div>
                                <div className="text-[10.5px]" style={{ color: isEmpty ? "#C2BCB2" : "#9A968F" }}>{b.count > 0 ? (hideFinancials ? `${b.count} project${b.count === 1 ? "" : "s"}` : `${gbpK(b.value)} value`) : "upcoming"}</div>
                            </div>
                            <div className="flex-1 h-7 rounded-full relative overflow-hidden" style={{ background: isEmpty ? "#F6F4F0" : "#EFEAE3" }}>
                                {b.count > 0 && (
                                    <div className="h-full rounded-full flex items-center px-3 transition-[width] duration-500"
                                        style={{ width: `${widthPct}%`, background: b.color }}>
                                        <span className="text-[11px] font-bold text-white whitespace-nowrap">
                                            {widthPct >= 30 ? `${b.count} project${b.count === 1 ? "" : "s"}` : b.count}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="w-5 text-right text-[15px] font-bold" style={{ color: b.count > 0 ? "#1F1F1F" : "#D8D3CA" }}>{b.count}</div>
                        </div>
                    );
                })}
            </div>
        </CardShell>
    );
}

function TimelineCard({ projects, clientMap, onOpen, hideFinancials = false }) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const windowStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const windowEnd = new Date(today.getFullYear(), today.getMonth() + 6, 1);
    const span = windowEnd - windowStart;
    const pct = (d) => Math.max(0, Math.min(100, ((d - windowStart) / span) * 100));
    const todayPct = pct(today);
    // Keep the "Today" pill inside the track at narrow (portal) widths too.
    const todayPillTransform = todayPct < 8 ? "translateX(0)" : todayPct > 92 ? "translateX(-100%)" : "translateX(-50%)";

    const months = Array.from({ length: 6 }, (_, i) => new Date(today.getFullYear(), today.getMonth() + i, 1));

    const rows = projects
        .filter((p) => p.target_date)
        .sort((a, b) => a.target_date.localeCompare(b.target_date));

    return (
        <CardShell icon={CalendarRange} title="Delivery timeline"
            aside={`target dates · ${windowStart.getFullYear()}`} testId="projects-timeline-card">
            {rows.length === 0 ? (
                <div className="text-[12px] italic py-2" style={{ color: "#9A968F" }}>No target dates to plot.</div>
            ) : (
                <div className="overflow-x-auto">
                    <div className="min-w-[640px]">
                        {/* Month axis */}
                        <div className="flex items-center mb-1.5">
                            <div className="w-[210px] shrink-0" />
                            <div className="flex-1 relative h-5">
                                {months.map((m, i) => (
                                    <div key={i} className="absolute top-0 text-[10.5px] font-semibold" style={{ left: `${(i / 6) * 100}%`, color: "#9A968F" }}>
                                        {m.toLocaleDateString("en-GB", { month: "short" })}
                                    </div>
                                ))}
                            </div>
                        </div>
                        {rows.map((p) => {
                            const tgt = parseDate(p.target_date);
                            const st = parseDate(p.start_date) || today;
                            const endX = pct(tgt);
                            const isComplete = p.status === "Complete";
                            const isOnHold = p.status === "On Hold";
                            const overdue = tgt < today && !isComplete;
                            const drawStart = new Date(Math.max(today.getTime(), st.getTime()));
                            const startX = pct(drawStart);
                            const hasBar = tgt >= today;
                            const dotColor = isComplete ? "#2E7D4F" : overdue ? "#C0392B" : isOnHold ? "#C68A1C" : "#1f2a44";
                            const c = clientMap[p.client_id];
                            const labelLeft = endX > 66; // flip label inward when dot sits in the right third
                            return (
                                <div key={p.id} data-testid={`timeline-row-${p.id}`} onClick={() => onOpen && onOpen(p.id)}
                                    className="flex items-center border-t cursor-pointer hover:bg-[#FCFBF9] transition-colors" style={{ borderColor: "#F4F1EC" }}>
                                    <div className="w-[210px] shrink-0 py-2.5 pr-3">
                                        <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate" title={p.name}>{trunc(p.name, 28)}</div>
                                        <div className="text-[10.5px] font-mono truncate" style={{ color: "#9A968F" }}>
                                            {p.reference}{c ? ` · ${c.branding?.logo_initials || c.name}` : ""}{p.lead_consultant ? ` · ${p.lead_consultant}` : ""}
                                        </div>
                                    </div>
                                    <div className="flex-1 relative h-12">
                                        {/* month gridlines */}
                                        {months.map((m, i) => (
                                            <div key={i} className="absolute top-1 bottom-1 border-l" style={{ left: `${(i / 6) * 100}%`, borderColor: "#F4F1EC" }} />
                                        ))}
                                        {/* today marker + pill */}
                                        <div className="absolute top-0 bottom-0" style={{ left: `${todayPct}%`, width: 2, background: "var(--brand-accent,#C75A1B)" }} />
                                        <div className="absolute top-0.5 text-[8.5px] font-bold px-1 py-[1px] rounded" style={{ left: `${todayPct}%`, transform: todayPillTransform, background: "#FBEFE5", color: "var(--brand-accent,#C75A1B)" }}>Today</div>
                                        {/* bar */}
                                        {hasBar && (
                                            <div className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full transition-[width] duration-500"
                                                style={{
                                                    left: `${startX}%`, width: `${Math.max(endX - startX, 1)}%`,
                                                    background: isOnHold ? "repeating-linear-gradient(45deg,#FBEFD8,#FBEFD8 5px,#F6E2BC 5px,#F6E2BC 10px)" : "linear-gradient(90deg,#1f2a44,#3a4a6b)",
                                                    border: isOnHold ? "1px dashed #C68A1C" : "none",
                                                }} />
                                        )}
                                        {/* end dot */}
                                        <div className="absolute top-1/2 -translate-y-1/2 grid place-items-center rounded-full"
                                            style={{ left: `${endX}%`, transform: "translate(-50%,-50%)", width: 16, height: 16, background: dotColor }}>
                                            {isComplete && <CheckCircle2 className="w-3 h-3 text-white" />}
                                        </div>
                                        {/* end label */}
                                        <div className="absolute top-1/2 -translate-y-1/2 text-[10.5px] font-semibold whitespace-nowrap"
                                            style={{ left: `${endX}%`, transform: `translate(${labelLeft ? "calc(-100% - 12px)" : "12px"}, -50%)`, color: "#3e4b63" }}>
                                            {tgt.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}{hideFinancials ? "" : ` · ${gbpK(p.value_gbp)}`}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {/* legend */}
                        <div className="flex items-center gap-5 mt-4 text-[11px]" style={{ color: "#6F6A64" }}>
                            <span className="inline-flex items-center gap-1.5"><span className="w-4 h-2 rounded-full" style={{ background: "linear-gradient(90deg,#1f2a44,#3a4a6b)" }} /> Active delivery</span>
                            <span className="inline-flex items-center gap-1.5"><span className="w-4 h-2 rounded-full" style={{ background: "repeating-linear-gradient(45deg,#FBEFD8,#FBEFD8 4px,#F6E2BC 4px,#F6E2BC 8px)", border: "1px dashed #C68A1C" }} /> On hold</span>
                            <span className="inline-flex items-center gap-1.5"><span className="w-[2px] h-3.5" style={{ background: "var(--brand-accent,#C75A1B)" }} /> Today</span>
                        </div>
                    </div>
                </div>
            )}
        </CardShell>
    );
}
